// Shared navigation / permission helpers.
//
// These used to live inside Layout.js. They were extracted so the Welcome
// launchpad can apply the EXACT same visibility rules as the sidebar (and so
// the two never drift). Layout.js imports `isItemVisible` + `GROUP_COLORS`
// from here; WelcomePage uses them to filter its curated utility tiles.
import { canUseBusinessIntelligence } from '@/lib/featureAccess';

// Roles that always see CEO/leadership-only items regardless of DB permissions.
export const CEO_ROLES = ['ceo', 'md', 'leadership', 'coo', 'admin'];

// Per-group colour ladder. Each sidebar group (and each launchpad tile, keyed
// by the same group label) gets a distinct hue so the UI reads as a quick
// colour map. tint/ring/ink power the collapsed-icon chips and tile accents.
export const GROUP_COLORS = {
  Overview:  { tint: 'bg-indigo-50',  ring: 'ring-indigo-100',  ink: 'text-indigo-600',  inkActive: 'text-indigo-700'  },
  Operations:{ tint: 'bg-sky-50',     ring: 'ring-sky-100',     ink: 'text-sky-600',     inkActive: 'text-sky-700'     },
  People:    { tint: 'bg-violet-50',  ring: 'ring-violet-100',  ink: 'text-violet-600',  inkActive: 'text-violet-700'  },
  Inventory: { tint: 'bg-amber-50',   ring: 'ring-amber-100',   ink: 'text-amber-600',   inkActive: 'text-amber-700'   },
  Purchase:  { tint: 'bg-orange-50',  ring: 'ring-orange-100',  ink: 'text-orange-600',  inkActive: 'text-orange-700'  },
  Finance:   { tint: 'bg-emerald-50', ring: 'ring-emerald-100', ink: 'text-emerald-600', inkActive: 'text-emerald-700' },
  Expenses:  { tint: 'bg-lime-50',    ring: 'ring-lime-100',    ink: 'text-lime-600',    inkActive: 'text-lime-700'    },
  Reports:   { tint: 'bg-cyan-50',    ring: 'ring-cyan-100',    ink: 'text-cyan-600',    inkActive: 'text-cyan-700'    },
  Analytics: { tint: 'bg-teal-50',    ring: 'ring-teal-100',    ink: 'text-teal-600',    inkActive: 'text-teal-700'    },
  Tasks:     { tint: 'bg-pink-50',    ring: 'ring-pink-100',    ink: 'text-pink-600',    inkActive: 'text-pink-700'    },
  CRM:       { tint: 'bg-rose-50',    ring: 'ring-rose-100',    ink: 'text-rose-600',    inkActive: 'text-rose-700'    },
  'Van Expense': { tint: 'bg-fuchsia-50', ring: 'ring-fuchsia-100', ink: 'text-fuchsia-600', inkActive: 'text-fuchsia-700' },
  Settings:  { tint: 'bg-slate-100',  ring: 'ring-slate-200',   ink: 'text-slate-600',   inkActive: 'text-slate-800'   },
  Help:      { tint: 'bg-blue-50',    ring: 'ring-blue-100',    ink: 'text-blue-600',    inkActive: 'text-blue-700'    },
};

// Decide whether a nav/tile item is visible to the current user.
//   - ceoOnly        → leadership roles (or superadmin) only
//   - superadminOnly → genuinely sensitive admin items (audit log, etc.)
//   - kshitijOnly    → preview/beta feature token gate
//   - menuKey(s)     → backend-driven menu visibility (the common case)
// An item with no flags and no menuKey is always visible (e.g. Help, Home).
export const isItemVisible = (item, isMenuVisible, isSuperadmin, user, displayRole) => {
  if (item.ceoOnly) {
    const r = (displayRole || '').toLowerCase();
    return isSuperadmin || CEO_ROLES.includes(r);
  }
  // Finance-Control-only items (the OFM operations page for Chitra). Superadmin
  // keeps the standard break-glass view.
  if (item.financeControlOnly) {
    return isSuperadmin || (displayRole || '').toLowerCase() === 'finance_control';
  }
  if (item.superadminOnly && !isSuperadmin) {
    return false;
  }
  if (item.kshitijOnly && !canUseBusinessIntelligence(user)) {
    return false;
  }

  // Optional role deny-list (e.g. hide internal pages from external portal accounts).
  if (Array.isArray(item.excludeRoles)) {
    const r = (user?.role || displayRole || '').toLowerCase();
    if (item.excludeRoles.includes(r)) return false;
  }

  const menuKeys = Array.isArray(item.menuKeys)
    ? item.menuKeys
    : item.menuKey
      ? [item.menuKey]
      : [];

  if (menuKeys.length > 0 && !menuKeys.some((menuKey) => isMenuVisible(menuKey))) {
    return false;
  }

  return true;
};
